import { Injectable } from '@angular/core';
import {
  getSolarRentBatteryBrand,
  getSolarRentBatteryModel,
  getSolarRentInverterLabel,
  getSolarRentMonitoringLabel,
  SOLAR_RENT_OCCUPATION_STATUS_VALUES,
  SOLAR_RENT_PERIODIC_PAYMENT_MODE_VALUES,
  SOLAR_RENT_PAYMENT_FREQUENCY_VALUES,
  getSolarRentPackageLabel,
  getSolarRentPackageObservations,
  SOLAR_RENT_TENANT_CIVILITY_VALUES,
  SOLAR_RENT_TENANT_COMPLETION_MENTION,
} from './solar-rent-wizard.config';
import type { SolarRentWizardStateService } from './solar-rent-wizard.state';

@Injectable({ providedIn: 'root' })
export class SolarRentWizardAutofillService {
  fill(state: SolarRentWizardStateService): void {
    const today = this.toDateInputValue(new Date());
    const form = state.form;
    const selectedContractType = state.getContractTypeDefinition(
      state.contractTypeOptions[1] ?? state.contractTypeOptions[0] ?? form.contractType,
    );
    const productData = selectedContractType?.productData ?? state.selectedProductData;
    const packageDetails = getSolarRentPackageObservations(productData);

    if (selectedContractType) {
      state.setProductType(selectedContractType.name);
    }

    form.latitude = '-20.1669';
    form.longitude = '57.5023';
    form.address.number = '12';
    form.address.street = 'Royal Road';
    form.address.city = 'Port-Louis';
    form.address.postcode = '11328';
    form.address.country = 'Mauritius';

    form.contractDetails.contractReference = 'SR-2026-001';
    form.contractDetails.fileReference = 'SRH-2026-001';
    form.contractDetails.tenantClient = 'Jean Martin';
    form.contractDetails.signatureDate = today;
    form.contractDetails.installationDate = today;
    form.contractDetails.commissioningDate = today;

    form.contractDetails.parties.landlord.legalName = 'Solar Rent LTD';
    form.contractDetails.parties.landlord.legalForm = 'Domestic company limited by shares';
    form.contractDetails.parties.landlord.address = 'Royal Road, Plaine Lauzun, Port-Louis, Mauritius';
    form.contractDetails.parties.landlord.phone = '+230 5 123 4567';
    form.contractDetails.parties.landlord.email = 'contact@solarrent.mu';
    form.contractDetails.parties.landlord.businessRegistrationNumber = 'C25226881';
    form.contractDetails.parties.landlord.representedBy = 'Richard Biron';
    form.contractDetails.parties.landlord.title = 'Directeur général';

    form.contractDetails.parties.technician.legalName = 'Solar Center LTD';
    form.contractDetails.parties.technician.legalForm = 'Domestic company limited by shares';
    form.contractDetails.parties.technician.address = 'Royal Road, Plaine Lauzun, Port-Louis, Mauritius';
    form.contractDetails.parties.technician.phone = '+230 5 234 5678';
    form.contractDetails.parties.technician.email = 'support@solarcenter.mu';
    form.contractDetails.parties.technician.businessRegistrationNumber = 'C11100437';
    form.contractDetails.parties.technician.representedBy = 'Richard Biron';
    form.contractDetails.parties.technician.title = 'Directeur général';

    form.contractDetails.parties.tenant.civility = SOLAR_RENT_TENANT_CIVILITY_VALUES[0];
    form.contractDetails.parties.tenant.name = 'Jean Martin';
    form.contractDetails.parties.tenant.address = '12 Royal Road, Port-Louis, Mauritius';
    form.contractDetails.parties.tenant.identityNumber = 'N123456789012';
    form.contractDetails.parties.tenant.phone = '5777 8899';
    form.contractDetails.parties.tenant.email = 'jean.martin@example.com';

    form.contractDetails.parties.site.fullAddress = state.siteCompleteAddress;
    form.contractDetails.parties.site.propertyType = 'Maison individuelle';
    form.contractDetails.parties.site.occupationStatus = SOLAR_RENT_OCCUPATION_STATUS_VALUES[0];
    form.contractDetails.parties.site.roofCondition = 'Bon etat apparent';
    form.contractDetails.parties.site.structureType = 'Charpente metallique legere';
    form.contractDetails.parties.site.generalInstallationMode = 'Pose en surimposition sur toiture principale';
    form.contractDetails.parties.site.roofAge = '8 ans';
    form.contractDetails.parties.site.roofLoadCapacity = 'Compatible pose solaire residentielle';
    form.contractDetails.parties.site.architecturalRestrictions = 'Aucune restriction connue';
    form.contractDetails.parties.site.siteConstraints = 'Acces standard au toit et au local technique';
    form.contractDetails.parties.site.easements = 'Aucune servitude bloquante declaree';
    form.contractDetails.parties.site.neighborConsents = 'Non applicable';
    this.setExclusiveSelection(
      form.contractDetails.parties.site,
      'singlePhaseEnvironment',
      ['singlePhaseEnvironment', 'threePhaseEnvironment'],
    );

    form.contractDetails.referenceConfig.range = selectedContractType?.label ?? selectedContractType?.name ?? '';
    form.contractDetails.dossierParticularities = 'Raccordement CEB a coordonner avec le calendrier client.';
    form.contractDetails.referenceConfig.commercialReference = state.selectedReferenceCommercialLabel || 'SRH 16 SP';
    form.contractDetails.referenceConfig.plannedPvPower = productData?.totalPvPower ?? '';
    form.contractDetails.referenceConfig.plannedBatteryCapacity = productData?.batteryCapacity ?? '';
    form.contractDetails.referenceConfig.plannedInverter = getSolarRentInverterLabel(productData);
    form.contractDetails.referenceConfig.previousContractReference = '';
    form.contractDetails.referenceConfig.incidentReportingPhone = '+230 5 555 0101';
    form.contractDetails.referenceConfig.incidentReportingEmail = 'support@solarhorizon.mu';
    form.contractDetails.referenceConfig.incidentReportingPlatform = 'Application mobile Solar Horizon';
    this.setExclusiveSelection(
      form.contractDetails.referenceConfig,
      'validationTechnicalYes',
      ['validationTechnicalYes', 'validationTechnicalNo'],
    );
    form.contractDetails.referenceConfig.validationTechnicalObservation = 'Validation favorable sous reserve du rapport final de visite.';
    this.setExclusiveSelection(
      form.contractDetails.referenceConfig,
      'firstRentFinancingYes',
      ['firstRentFinancingYes', 'firstRentFinancingNo'],
    );
    form.contractDetails.referenceConfig.firstRentFinancingInstitution = 'MCB';
    this.setExclusiveSelection(
      form.contractDetails.referenceConfig,
      'cebHouseholdsSchemeYes',
      ['cebHouseholdsSchemeYes', 'cebHouseholdsSchemeNo'],
    );
    form.contractDetails.referenceConfig.cebHouseholdsSchemeObservation = 'Dossier households en cours de confirmation aupres du CEB.';
    this.setExclusiveSelection(
      form.contractDetails.referenceConfig,
      'tenantRequiredDocumentsYes',
      ['tenantRequiredDocumentsYes', 'tenantRequiredDocumentsNo'],
    );
    form.contractDetails.referenceConfig.tenantRequiredDocumentsObservation = 'Toutes les pieces justificatives ont ete transmises.';
    form.contractDetails.referenceConfig.otherSuspensiveConditions = 'Aucune autre condition suspensive particuliere identifiee.';
    form.contractDetails.referenceConfig.suspensiveConditions = 'Validation technique du Site';
    form.contractDetails.referenceConfig.suspensiveDetails = 'Sous reserve de validation finale du site, du financement du premier loyer, des demarches CEB et des pieces requises.';
    form.contractDetails.referenceConfig.regulatoryApprovals = 'Aucune approbation complementaire requise a ce stade.';
    form.contractDetails.referenceConfig.otherUsefulDetails = 'Precisions contractuelles utiles pour le dossier pilote.';

    this.setExclusiveSelection(
      form.contractDetails.referenceConfig,
      productData?.monitoringIncluded ? 'plannedMonitoringYes' : 'plannedMonitoringNo',
      ['plannedMonitoringYes', 'plannedMonitoringNo'],
    );

    form.contractDetails.financialTerms.initialMonthlyRentTtc = 'Rs 4,950';
    form.contractDetails.financialTerms.firstRentScheduleInstallment2Date = today;
    form.contractDetails.financialTerms.firstRentScheduleInstallment3Amount = 'Rs 7,500';
    form.contractDetails.financialTerms.firstRentScheduleInstallment3Date = today;
    form.contractDetails.financialTerms.firstRentExternalFinancingInstitution = 'MCB Leasing';
    form.contractDetails.financialTerms.firstRentExternalAmount = 'Rs 25,000';
    form.contractDetails.financialTerms.firstRentExternalIndicativeMonthlyPayment = 'Rs 2,250';
    form.contractDetails.financialTerms.firstRentExternalDuration = '12 mois';
    form.contractDetails.financialTerms.firstRentExternalDisbursementDate = today;
    form.contractDetails.financialTerms.firstPaymentDueDate = today;
    form.contractDetails.financialTerms.paymentFrequency = SOLAR_RENT_PAYMENT_FREQUENCY_VALUES[0];
    form.contractDetails.financialTerms.periodicPaymentMode = SOLAR_RENT_PERIODIC_PAYMENT_MODE_VALUES[0];
    form.contractDetails.financialTerms.periodicPaymentModeDirectDebit = true;
    form.contractDetails.financialTerms.deferredFinancialStartDate = today;
    form.contractDetails.financialTerms.deferredFinancialStartReason = 'Demarrage aligne sur la date de raccordement CEB et la disponibilite client.';
    form.contractDetails.financialTerms.rentRevisionFormula = 'Revision annuelle selon indice contractuel applicable';
    form.contractDetails.financialTerms.latePaymentPenalty = 'Interets de retard selon contrat';
    form.contractDetails.financialTerms.securityDepositAmount = 'Rs 0';
    form.contractDetails.financialTerms.insuranceResponsibleBy = 'Locataire';
    form.contractDetails.financialTerms.insuranceAmount = 'Inclus selon police du site';
    form.contractDetails.financialTerms.earlyTerminationPenalty = 'Indemnite selon conditions particulieres';

    form.contractDetails.mandatesPayment.cebMeterReference = 'CEB-99887766';
    form.contractDetails.mandatesPayment.cebCustomerNumber = 'CUST-554433';
    form.contractDetails.mandatesPayment.cebMandateScope = 'Demarches CEB, administratives, techniques et de raccordement';
    form.contractDetails.mandatesPayment.cebMandateStartDate = today;
    form.contractDetails.mandatesPayment.cebMandateEndDate = '';
    form.contractDetails.mandatesPayment.cebMandateDuration = '12';
    form.contractDetails.mandatesPayment.cebReferenceDate = today;
    form.contractDetails.mandatesPayment.cebReferenceTariff = 'Tarif domestique standard';
    form.contractDetails.mandatesPayment.cebTariffCategory = 'Domestic';
    form.contractDetails.mandatesPayment.cebReferenceKwhValue = '6.45';
    form.contractDetails.mandatesPayment.cebApplicableScheme = 'Net metering residentiel';
    form.contractDetails.mandatesPayment.bankName = 'Mauritius Commercial Bank';
    form.contractDetails.mandatesPayment.iban = 'MU43MCBL1234567890123456789012';
    form.contractDetails.mandatesPayment.accountHolder = 'Jean Martin';
    this.setExclusiveSelection(
      form.contractDetails.mandatesPayment,
      'directDebitAuthorizationYes',
      ['directDebitAuthorizationYes', 'directDebitAuthorizationNo'],
    );
    form.contractDetails.mandatesPayment.paymentModePrimary = 'Versement direct';
    form.contractDetails.mandatesPayment.paymentModeSecondary = 'Virement bancaire';
    form.contractDetails.mandatesPayment.mandateSignatureDate = today;

    form.contractDetails.warrantiesSla.maintenanceSchedule = 'Maintenance preventive semestrielle';
    form.contractDetails.warrantiesSla.maintenanceResponseTime = '48h ouvrables';
    form.contractDetails.warrantiesSla.restorationTime = 'Sous 5 jours ouvrables selon disponibilite pieces';
    form.contractDetails.warrantiesSla.maintenanceExclusions = 'Degats causes par tiers, force majeure, modifications non autorisees';
    form.contractDetails.warrantiesSla.availabilityGuarantee = 'Disponibilite cible sous reserve du reseau et des exclusions contractuelles';
    form.contractDetails.warrantiesSla.downtimeCompensation = 'Avoir ou ajustement selon conditions d indisponibilite';
    form.contractDetails.warrantiesSla.batteryWarrantyCycles = 'Selon fabricant';
    form.contractDetails.warrantiesSla.batteryWarrantyDuration = '10 ans';
    form.contractDetails.warrantiesSla.performanceWarranty = 'Performance attendue selon etude et conditions du site';
    form.contractDetails.warrantiesSla.tenantObligations = 'Assurer acces au site, alimentation disponible et signalement des incidents';
    form.contractDetails.warrantiesSla.providerObligations = 'Maintenance, suivi et support selon contrat';
    form.contractDetails.installedConfig.summary.plannedPv = productData?.totalPvPower ?? '';
    form.contractDetails.installedConfig.summary.installedPv = productData?.totalPvPower ?? '';
    form.contractDetails.installedConfig.summary.plannedBattery = productData?.batteryCapacity ?? '';
    form.contractDetails.installedConfig.summary.installedBattery = productData?.batteryCapacity ?? '';
    form.contractDetails.installedConfig.summary.plannedModuleCount = productData?.panelCount ?? '';
    form.contractDetails.installedConfig.summary.installedModuleCount = productData?.panelCount ?? '';
    form.contractDetails.installedConfig.summary.plannedInverter = getSolarRentInverterLabel(productData);
    form.contractDetails.installedConfig.summary.installedInverter = getSolarRentInverterLabel(productData);
    form.contractDetails.installedConfig.summary.plannedMonitoring = getSolarRentMonitoringLabel(productData);
    form.contractDetails.installedConfig.summary.installedMonitoring = getSolarRentMonitoringLabel(productData);
    form.contractDetails.installedConfig.summary.plannedEnvironment = 'SP';
    form.contractDetails.installedConfig.summary.installedEnvironment = 'SP';
    this.setExclusiveSelection(form.contractDetails.installedConfig.summary, 'pvCompliant', ['pvCompliant', 'pvAdapted']);
    this.setExclusiveSelection(form.contractDetails.installedConfig.summary, 'batteryCompliant', ['batteryCompliant', 'batteryAdapted']);
    this.setExclusiveSelection(form.contractDetails.installedConfig.summary, 'moduleCountCompliant', ['moduleCountCompliant', 'moduleCountAdapted']);
    this.setExclusiveSelection(form.contractDetails.installedConfig.summary, 'inverterCompliant', ['inverterCompliant', 'inverterAdapted']);
    this.setExclusiveSelection(form.contractDetails.installedConfig.summary, 'monitoringCompliant', ['monitoringCompliant', 'monitoringAdapted']);
    this.setExclusiveSelection(form.contractDetails.installedConfig.summary, 'environmentCompliant', ['environmentCompliant', 'environmentAdapted']);
    form.contractDetails.installedConfig.generalComment = packageDetails;

    form.contractDetails.pvModules.brand = 'Generic solar module';
    form.contractDetails.pvModules.model = productData?.panelUnitPower ? `${productData.panelUnitPower} W` : '';
    form.contractDetails.pvModules.unitPower = productData?.panelUnitPower ?? '';
    form.contractDetails.pvModules.moduleCount = productData?.panelCount ?? '';
    form.contractDetails.pvModules.totalPower = productData?.totalPvPower ?? '';
    form.contractDetails.pvModules.serialNumbers = Array.from(
      { length: Number.parseInt(form.contractDetails.pvModules.moduleCount || '0', 10) || 0 },
      (_, index) => `PV-2026-${String(index + 1).padStart(3, '0')}`,
    ).join(', ');
    form.contractDetails.pvModules.moduleTableRows = Array.from(
      { length: Number.parseInt(form.contractDetails.pvModules.moduleCount || '0', 10) || 0 },
      (_, index) => ({
        key: String(index + 1),
        value: {
          brand: form.contractDetails.pvModules.brand,
          model: form.contractDetails.pvModules.model,
          unitPower: form.contractDetails.pvModules.unitPower,
          serialNumber: `PV-2026-${String(index + 1).padStart(3, '0')}`,
        },
      }),
    ).reduce<Record<string, { brand: string; model: string; unitPower: string; serialNumber: string }>>((rows, entry) => {
      rows[entry.key] = entry.value;
      return rows;
    }, {});
    form.contractDetails.pvModules.observations = packageDetails;

    form.contractDetails.inverter.brand = getSolarRentBatteryBrand();
    form.contractDetails.inverter.model = getSolarRentInverterLabel(productData);
    form.contractDetails.inverter.nominalPower = productData?.inverterPower ?? '';
    this.setExclusiveSelection(form.contractDetails.inverter, 'hybridType', ['hybridType', 'otherType']);
    form.contractDetails.inverter.otherTypeDetails = '';
    form.contractDetails.inverter.serialNumber = 'INV-2026-0001';
    form.contractDetails.inverter.location = 'Utility room';
    form.contractDetails.inverter.observations = packageDetails;

    form.contractDetails.battery.brand = getSolarRentBatteryBrand();
    form.contractDetails.battery.model = getSolarRentBatteryModel();
    form.contractDetails.battery.nominalCapacity = productData?.batteryCapacity ?? '';
    form.contractDetails.battery.usableCapacity = productData?.batteryCapacity ?? '';
    form.contractDetails.battery.unitCount = productData?.batteryUnitCount ?? '';
    form.contractDetails.battery.serialNumbers = this.buildBatterySerialNumbers(Number(productData?.batteryUnitCount ?? 0));
    form.contractDetails.battery.batteryTableRows = Array.from(
      { length: Number.parseInt(form.contractDetails.battery.unitCount || '0', 10) || 0 },
      (_, index) => ({
        key: String(index + 1),
        value: {
          brand: form.contractDetails.battery.brand,
          model: form.contractDetails.battery.model,
          capacity: form.contractDetails.battery.nominalCapacity,
          serialNumber: `BAT-2026-${String(index + 1).padStart(3, '0')}`,
        },
      }),
    ).reduce<Record<string, { brand: string; model: string; capacity: string; serialNumber: string }>>((rows, entry) => {
      rows[entry.key] = entry.value;
      return rows;
    }, {});
    form.contractDetails.battery.location = 'Utility room';
    form.contractDetails.battery.observations = packageDetails;

    form.contractDetails.protections.acProtections = true;
    form.contractDetails.protections.dcProtections = true;
    form.contractDetails.protections.disconnectionBox = true;
    form.contractDetails.protections.surgeProtection = true;
    form.contractDetails.protections.mainWiring = true;
    form.contractDetails.protections.connectors = true;
    form.contractDetails.protections.communicationInterfaces = true;
    form.contractDetails.protections.gridMeteringInterface = true;
    form.contractDetails.protections.gridMeteringInterfaceObservation = 'Interface de comptage bidirectionnel installee et fonctionnelle';
    form.contractDetails.protections.mountingElements = true;
    form.contractDetails.protections.signage = true;
    form.contractDetails.protections.other = false;
    form.contractDetails.protections.observations = 'Protection devices and labeling verified.';

    this.setExclusiveSelection(form.contractDetails.monitoring, 'installedYes', ['installedYes', 'installedNo']);
    form.contractDetails.monitoring.description = 'Monitoring energetique et supervision de performance';
    form.contractDetails.monitoring.application = getSolarRentMonitoringLabel(productData);
    this.setExclusiveSelection(
      form.contractDetails.monitoring,
      'connectivityWifi',
      ['connectivityWifi', 'connectivity4g', 'connectivityEthernet', 'connectivityOther'],
    );
    form.contractDetails.monitoring.connectivityOtherDetails = '';
    this.setExclusiveSelection(form.contractDetails.monitoring, 'accountCreatedYes', ['accountCreatedYes', 'accountCreatedNo']);
    this.setExclusiveSelection(form.contractDetails.monitoring, 'accessProvidedYes', ['accessProvidedYes', 'accessProvidedNo']);
    this.setExclusiveSelection(
      form.contractDetails.monitoring,
      'reportingTestYes',
      ['reportingTestYes', 'reportingTestNo', 'reportingTestNotApplicable'],
    );
    form.contractDetails.monitoring.testingProtocol = 'Connexion, remontee des donnees et alertes verifiees';
    form.contractDetails.monitoring.productionBaseline = 'Production de reference selon etude technique';
    form.contractDetails.monitoring.observations = packageDetails;

    form.contractDetails.siteLayout.modules.mainRoof = true;
    form.contractDetails.siteLayout.modules.secondaryRoof = false;
    form.contractDetails.siteLayout.modules.dedicatedStructure = false;
    form.contractDetails.siteLayout.modules.other = false;
    form.contractDetails.siteLayout.modules.otherDetails = '';
    form.contractDetails.siteLayout.modules.orientation = '15';
    form.contractDetails.siteLayout.modules.tilt = '20';
    this.setExclusiveSelection(
      form.contractDetails.siteLayout.modules,
      'shadingNo',
      ['shadingYes', 'shadingNo'],
    );
    form.contractDetails.siteLayout.modules.observations = 'No visible shading on installation area.';

    form.contractDetails.siteLayout.equipment.batteryLocation = 'Utility room';
    form.contractDetails.siteLayout.equipment.inverterLocation = 'Utility room';
    form.contractDetails.siteLayout.equipment.enclosureLocation = 'Near main DB';
    this.setExclusiveSelection(
      form.contractDetails.siteLayout.equipment,
      'maintenanceSatisfactory',
      ['maintenanceSatisfactory', 'maintenanceNeedsMonitoring', 'maintenanceReserved'],
    );
    form.contractDetails.siteLayout.equipment.maintenanceAccessConstraints = 'Acces maintenance libre en journee avec prise de rendez-vous';
    form.contractDetails.siteLayout.equipment.observations = 'Maintenance access is clear and safe.';

    this.setExclusiveSelection(
      form.contractDetails.deviations.declaration,
      'none',
      ['none', 'minor', 'substantial'],
    );
    form.contractDetails.deviations.minor = '';
    form.contractDetails.deviations.substantial = '';
    form.contractDetails.deviations.minorTableRows = {
      '1': {
        element: 'Implantation des modules',
        plannedConfiguration: 'Toiture principale',
        installedConfiguration: 'Toiture secondaire',
        justification: 'Adaptation mineure selon configuration reelle du site',
      },
    };
    form.contractDetails.deviations.substantialDeviation = {
      element: 'Puissance onduleur',
      description: 'Onduleur installe de 3.6 kVA au lieu de 5 kVA prevu',
      writtenAgreement: true,
      srhcpAmendment: true,
      contractualAmendment: false,
      other: true,
      otherDetails: 'Validation technique du concepteur',
      acceptanceDocumentReference: 'PROCES-VERBAL-ATTI-001',
    };

    this.setExclusiveSelection(
      form.contractDetails.reservations.declaration,
      'withoutReservation',
      ['withoutReservation', 'withMinorReservations', 'withSubstantialReservations'],
    );
    form.contractDetails.reservations.minorReservations = '';
    form.contractDetails.reservations.minorTreatmentModalities = '';
    form.contractDetails.reservations.substantialReservations = '';
    this.setExclusiveSelection(
      form.contractDetails.reservations.actions,
      'commissioningMaintained',
      ['commissioningMaintained', 'partialCommissioning', 'commissioningPostponed', 'amendmentRequired'],
    );
    form.contractDetails.reservations.punchListItems = 'Aucun point bloquant - controle final du monitoring a confirmer.';
    form.contractDetails.reservations.remediationDeadline = today;
    form.contractDetails.reservations.provisionalAcceptanceDate = today;
    form.contractDetails.reservations.definitiveAcceptanceDate = today;

    Object.values(form.contractDetails.commissioningChecks).forEach((row) => {
      this.setExclusiveSelection(row, 'conforme', ['conforme', 'reserve', 'notApplicable']);
    });

    form.contractDetails.documents.regulatoryApprovals = 'Dossier technique et accords reglementaires selon besoin.';
    form.contractDetails.documents.requiredDocumentsChecklist = 'Contrat signe, etude technique, annexes, mandat, pieces justificatives.';
    form.contractDetails.documents.otherDetails = packageDetails;
    form.contractDetails.additionalObservations.landlordTechnician = `Offre ${getSolarRentPackageLabel(productData)} appliquee. ${packageDetails}`;
    form.contractDetails.additionalObservations.tenant = `Client informe des elements inclus: ${packageDetails}`;

    form.contractDetails.signatures.date = today;
    form.contractDetails.signatures.landlord.name = 'Richard Biron';
    form.contractDetails.signatures.landlord.title = 'Directeur Général / General Manager';
    form.contractDetails.signatures.landlord.signature = this.createSignatureDataUrl('R. Biron', '#8e511c');
    form.contractDetails.signatures.technician.name = 'Richard Biron';
    form.contractDetails.signatures.technician.title = 'Directeur Général / General Manager';
    form.contractDetails.signatures.technician.signature = this.createSignatureDataUrl('R. Biron', '#8e511c');
    form.contractDetails.signatures.tenant.name = 'Jean Martin';
    form.contractDetails.signatures.tenant.mention = SOLAR_RENT_TENANT_COMPLETION_MENTION;
    form.contractDetails.signatures.tenant.signature = this.createSignatureDataUrl('J. Martin', '#241f1a');

    for (const entry of Object.values(state.signatureDocuments)) {
      entry.signatureDate = today;
      entry.signedSnapshot = null;
      entry.signedDocumentFile = null;
    }

    const finalSignature = state.signatureDocuments['signature-final'];
    if (finalSignature) {
      finalSignature.signatureMode = 'draw';
      finalSignature.drawnSignatureDataUrl = this.createSignatureDataUrl('J. Martin', '#241f1a');
      finalSignature.signatureUploadFile = null;
      finalSignature.signedSnapshot = null;
      finalSignature.signedDocumentFile = null;
    }
  }

  private setExclusiveSelection(target: Record<string, unknown>, selectedKey: string, keys: string[]): void {
    for (const key of keys) {
      target[key] = key === selectedKey;
    }
  }

  private toDateInputValue(date: Date): string {
    const pad = (value: number) => value.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  private buildBatterySerialNumbers(unitCount: number): string {
    return Array.from({ length: Math.max(unitCount, 0) }, (_, index) => `BAT-2026-${String(index + 1).padStart(3, '0')}`).join(', ');
  }

  private createSignatureDataUrl(label: string, color: string): string {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="280" viewBox="0 0 800 280"><rect width="800" height="280" fill="white"/><path d="M70 198 C130 118, 190 248, 256 154 S382 118, 438 182 560 238, 642 126" fill="none" stroke="${color}" stroke-width="8" stroke-linecap="round" stroke-linejoin="round"/><text x="84" y="238" fill="${color}" font-size="28" font-family="Georgia, serif">${label}</text></svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  }
}